import { useState } from "react";
import { MdOutlineTimeline } from "react-icons/md";

import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Select,
  Spinner,
  Switch,
  Table,
  Text,
  VStack,
} from "components/ui";
import { useAiTraces } from "hooks/useAiTraces";
import { useTranslation } from "hooks/useTranslation";
import { AiTraceTurn } from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

import TraceDetailModal from "./TraceDetailModal";

interface TracesPanelProps {
  /** Bumped by the page's Refresh button. Re-runs the queries without remounting the panel. */
  refreshToken: number;
}

/** The server clamps to 1–30; these are the windows worth offering. */
const WINDOWS = [1, 7, 30];
const PAGE_SIZE = 25;

const formatDateTime = (iso: string) => new Date(iso).toLocaleString();

const formatUsd = (value: number) => {
  if (value === 0) return "$0.00";
  if (Math.abs(value) < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDuration = (ms: number) =>
  ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;

/**
 * Traceability — every LLM call of a turn, with what it resolved to, what it retried, and what it cost.
 *
 * A row here is one **turn** (`batchId`), not one provider call: a single answer routinely spans a
 * guardrail pass, the main agent and any sub-agents, and each of those can retry or fall back. The
 * list therefore counts *attempts*, and the call-by-call split lives in the drill-down.
 *
 * Lives under Analytics → AI actions as the Activity tab and is shown to every tenant — turns are
 * traced whether or not the AI Gateway feature is on. The drill-down shows the call-by-call split
 * with a link to each call's AI-action detail; it never renders the stored request/response
 * payloads, which carry the platform's own instructions. This panel is read-only.
 */
const TracesPanel = ({ refreshToken }: TracesPanelProps) => {
  const { t } = useTranslation();
  const [days, setDays] = useState(WINDOWS[1]);
  const [page, setPage] = useState(1);
  const [failuresOnly, setFailuresOnly] = useState(false);
  const [openBatchId, setOpenBatchId] = useState<string | null>(null);

  const { turns, loading } = useAiTraces(
    days,
    page,
    PAGE_SIZE,
    failuresOnly,
    refreshToken,
  );

  const windowItems = WINDOWS.map((value) => ({
    value: String(value),
    label: t("app.aiGateway.traces.window.option", { days: value }),
  }));

  const applyFilter = (change: () => void) => {
    change();
    setPage(1);
  };

  const totalTurns = turns?.totalTurns ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalTurns / PAGE_SIZE));

  const renderTurnRow = (turn: AiTraceTurn) => (
    <Table.Row key={turn.batchId}>
      <Table.Cell>
        <VStack align="start" gap={0}>
          <Text fontSize="sm">{formatDateTime(turn.startedAt)}</Text>
          <Text fontSize="xs" color={getThemeColor("text.secondary")}>
            {turn.routes.length > 0
              ? turn.routes.join(", ")
              : t("app.aiGateway.traces.table.noRoute")}
          </Text>
        </VStack>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="sm">
          {turn.models.length > 0
            ? turn.models.join(", ")
            : t("app.aiGateway.traces.table.noModel")}
        </Text>
      </Table.Cell>
      <Table.Cell textAlign="end">
        <HStack gap={2} justify="flex-end">
          <Text fontSize="sm">{turn.attemptCount}</Text>
          {turn.failedAttemptCount > 0 && (
            <Badge colorPalette="red" variant="subtle">
              {t("app.aiGateway.traces.table.failedBadge", {
                count: turn.failedAttemptCount,
              })}
            </Badge>
          )}
        </HStack>
      </Table.Cell>
      <Table.Cell textAlign="end">
        {formatDuration(turn.providerDurationMs)}
      </Table.Cell>
      <Table.Cell textAlign="end">
        {(turn.inputTokens + turn.outputTokens).toLocaleString()}
      </Table.Cell>
      <Table.Cell textAlign="end">
        <VStack align="end" gap={0}>
          <Text fontSize="sm">{formatUsd(turn.costUsd)}</Text>
          {turn.unpricedCalls > 0 && (
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {t("app.aiGateway.traces.table.unpricedRow", {
                count: turn.unpricedCalls,
              })}
            </Text>
          )}
        </VStack>
      </Table.Cell>
      <Table.Cell textAlign="end">
        <Button
          intent="ghost"
          size="xs"
          onClick={() => setOpenBatchId(turn.batchId)}
        >
          {t("app.aiGateway.traces.table.view")}
        </Button>
      </Table.Cell>
    </Table.Row>
  );

  return (
    <VStack align="stretch" gap={5}>
      <Flex justify="space-between" align="flex-end" wrap="wrap" gap={3}>
        <Text
          fontSize="sm"
          color={getThemeColor("text.secondary")}
          maxW="560px"
        >
          {t("app.aiGateway.traces.subtitle")}
        </Text>
        <HStack gap={4} wrap="wrap">
          <HStack gap={2}>
            <Text fontSize="sm">
              {t("app.aiGateway.traces.filters.failuresOnly")}
            </Text>
            <Switch
              checked={failuresOnly}
              onCheckedChange={(e: any) =>
                applyFilter(() => setFailuresOnly(e.checked))
              }
              colorPalette="blue"
            />
          </HStack>
          <Select
            label={t("app.aiGateway.traces.window.label")}
            items={windowItems}
            value={[String(days)]}
            onValueChange={(d: { value: string[] }) =>
              applyFilter(() => setDays(Number(d.value[0])))
            }
            size="sm"
            w="180px"
          />
        </HStack>
      </Flex>

      <VStack align="stretch" gap={2}>
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.aiGateway.traces.table.title")}
        </Text>

        {loading && !turns ? (
          <Flex justify="center" py={10}>
            <Spinner />
          </Flex>
        ) : turns && turns.items.length > 0 ? (
          <>
            <Box overflowX="auto">
              <Table.Root size="sm" variant="outline" minW="860px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>
                      {t("app.aiGateway.traces.table.startedAt")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.aiGateway.traces.table.models")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end">
                      {t("app.aiGateway.traces.table.attempts")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end">
                      {t("app.aiGateway.traces.table.duration")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end">
                      {t("app.aiGateway.traces.table.tokens")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end">
                      {t("app.aiGateway.traces.table.cost")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end">
                      {t("app.aiGateway.traces.table.actions")}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>{turns.items.map(renderTurnRow)}</Table.Body>
              </Table.Root>
            </Box>

            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
              <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                {t("app.aiGateway.traces.table.pageInfo", {
                  page,
                  pageCount,
                  total: totalTurns,
                })}
              </Text>
              <HStack gap={2}>
                <Button
                  intent="secondary"
                  size="xs"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  {t("app.aiGateway.traces.table.previous")}
                </Button>
                <Button
                  intent="secondary"
                  size="xs"
                  disabled={page >= pageCount || loading}
                  onClick={() => setPage((current) => current + 1)}
                >
                  {t("app.aiGateway.traces.table.next")}
                </Button>
              </HStack>
            </Flex>
          </>
        ) : (
          <VStack gap={3} py={10}>
            <Box color={getThemeColor("text.secondary")} fontSize="3xl">
              <MdOutlineTimeline />
            </Box>
            <Text color={getThemeColor("text.secondary")} textAlign="center">
              {t("app.aiGateway.traces.empty")}
            </Text>
          </VStack>
        )}
      </VStack>

      <TraceDetailModal
        batchId={openBatchId}
        onClose={() => setOpenBatchId(null)}
      />
    </VStack>
  );
};

export default TracesPanel;
